import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Contact } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { useTranslation } from 'react-i18next';
import { FaxV1, FaxQueries, FaxTypes, FaxHooks } from '@frontend/api-fax';
import { useAppScopeStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ChipCombobox,
  ListRow,
  Modal,
  ModalControlModalProps,
  Text,
  phone,
  useAlert,
  useDebouncedValue,
} from '@frontend/design-system';
import { useSelectedFaxShallowStore } from '../../stores';

type ForwardModalProps = {
  locationId: string;
  forwardModalProps: ModalControlModalProps;
  faxId: string;
};

export const ForwardFaxModal = ({ locationId, forwardModalProps, faxId }: ForwardModalProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { show: isSlidePanelOpen } = useSlidePanelShallowStore('show');
  const { t } = useTranslation('fax');
  const alert = useAlert();

  const { invalidateListFaxes, addToListFaxCache } = FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const invalidateFaxContacts = FaxHooks.useInvalidateFaxContacts();

  const [showCustomMenuItem, setShowCustomMenuItem] = useState(false);
  const [contactSearchInput, setContactSearchInput] = useState('');
  const [to, setTo] = useState<Contact[]>([]);

  const debouncedContactSearchTerm = useDebouncedValue(contactSearchInput, 1000);

  const { data: savedContacts } = FaxQueries.useQueryFaxContacts({
    locationId,
    locationIds: selectedLocationIds,
    limit: 5,
    recentContacts: true,
  });
  const { data: searchContactsData } = FaxQueries.useSearchContactsInfiniteQuery({
    locationId,
    locationIds: selectedLocationIds,
    query: debouncedContactSearchTerm,
    enabled: debouncedContactSearchTerm.length > 0,
    limit: 25,
  });

  const allCurrentContacts = useMemo(() => {
    const savedContactsList = savedContacts ?? [];
    const searchedContactsList = searchContactsData?.pages?.flatMap((page) => page?.rows?.filter(Boolean)) ?? [];

    const seen = new Set<string>();
    const uniqueContacts: Contact[] = [];

    [...searchedContactsList, ...savedContactsList].forEach((contact) => {
      if (contact?.id && !seen.has(contact.id)) {
        seen.add(contact.id);
        uniqueContacts.push(contact);
      }
    });

    return uniqueContacts;
  }, [savedContacts, searchContactsData]);

  const { mutate: forwardFax, isLoading: isForwardingFax } = FaxQueries.useForwardFax({
    onSuccess: ({ faxes }) => {
      handleForwardModalClose();
      alert.success(t('Fax has been sent successfully'));
      setTimeout(() => {
        invalidateFaxContacts();
        if (!faxes || !faxes.length) {
          invalidateListFaxes();
          return;
        }
        faxes.forEach((fax) => {
          addToListFaxCache({ faxId: fax.id, locationId: fax.locationId });
        });
      }, 100);
    },
    onError: () => {
      alert.error(t('Failed to send fax. Please try again.'));
    },
  });

  const { mutate: upsertContact } = FaxQueries.useUpsertFaxContact({
    onSuccess: (newContact) => {
      invalidateListFaxes();
      setTo((prev) => [...prev, newContact]);
    },
    onError: () => {
      alert.error(t('Invalid fax number'));
    },
  });

  const handleForwardModalClose = () => {
    setTo([]);
    forwardModalProps.onClose();
  };

  const customOptionsFilter = (contact: Contact, inputValue: string) => {
    let formattedInput = inputValue.replace(/[\s-()]/g, '').toLowerCase();
    if (formattedInput.startsWith('+1')) {
      formattedInput = formattedInput.slice(2);
    } else if (formattedInput.startsWith('1')) {
      formattedInput = formattedInput.slice(1);
    }

    if (formattedInput.length === 10 && /^\d{10}$/.test(formattedInput)) {
      const filteredContacts = allCurrentContacts?.filter(
        (contact) => contact.faxNumber?.replace(/[\s-()]/g, '') === formattedInput
      );
      if (filteredContacts?.length === 0) {
        setShowCustomMenuItem(true);
      }
    } else {
      setShowCustomMenuItem(false);
    }

    const value = `${contact.name ?? ''} ${contact.secondName ?? ''} ${contact.faxNumber}`.replace(/[\s-()]/g, '');

    return value.toLowerCase().includes(formattedInput);
  };

  const addCustomContactToForward = (value: string) => {
    const existingContact = allCurrentContacts?.find(
      (contact) =>
        contact.faxNumber === value ||
        `${contact.name} ${contact.secondName}`.toLowerCase().includes(value.toLowerCase())
    );

    const number = phone(value, { format: 'numbers', hideCountry: true });

    const isNumeric = /^\d{10}$/.test(number);

    if (existingContact && existingContact.id) {
      setTo((prev) => [...prev, existingContact]);
      return;
    }

    if (isNumeric) {
      const contactData = {
        name: '',
        secondName: '',
        faxNumber: number ?? '',
        phoneNumber: '',
        profileMediaId: '',
        locationId,
      };

      upsertContact(contactData);
    } else {
      return alert.error(t('Invalid fax number'));
    }
    return;
  };

  const optionsToShow = useMemo(() => {
    return (
      (debouncedContactSearchTerm
        ? searchContactsData?.pages?.flatMap((page) => page?.rows?.filter(Boolean))
        : savedContacts) ?? []
    );
  }, [debouncedContactSearchTerm, savedContacts, searchContactsData]);

  return (
    !isSlidePanelOpen && (
      <Modal {...forwardModalProps} minWidth={480} onClose={handleForwardModalClose}>
        <Modal.Header>{t('Forward')}</Modal.Header>
        <Modal.Body css={{ paddingTop: theme.spacing(2) }}>
          {optionsToShow ? (
            <ChipCombobox<Contact>
              label={t('Select Recipients')}
              clearable={false}
              options={optionsToShow}
              tags={to}
              name='to'
              onTagsChange={(tags) => {
                if (tags.length > to.length && to.some((contact) => contact.id === tags[tags.length - 1]?.id)) {
                  alert.error(t('This contact is already selected'));
                  return;
                }
                setTo(tags);
              }}
              MenuItem={FaxContactItem}
              ChipComponent={ContactChip}
              onInputChange={(inputValue) => setContactSearchInput(inputValue)}
              accessor={(contact) => `${contact.name ?? ''} ${contact.secondName ?? ''} ${contact.faxNumber}`}
              itemHeight={62}
              itemWidth='100%'
              menuProps={{
                placement: 'bottom-start',
              }}
              menuStyles={css`
                width: min(100vw, 600px);
              `}
              onCustomSubmission={addCustomContactToForward}
              CustomSubmissionMenuItem={AddContactMenuItem}
              allowCustomSubmission={showCustomMenuItem}
              tabComplete
              customOptionsFilter={customOptionsFilter}
              disabled={isForwardingFax}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer
          primary={{
            label: t('Forward'),
            loading: isForwardingFax,
            disabled: to.length === 0 || isForwardingFax,
            onClick: () => {
              forwardFax({
                faxId,
                locationId,
                contactIds: to.map((contact) => contact.id),
              });
            },
          }}
          secondary={{
            label: t('Cancel'),
            onClick: handleForwardModalClose,
            disabled: isForwardingFax,
          }}
        />
      </Modal>
    )
  );
};

type FaxContactItemProps = { item: Contact };
const FaxContactItem = ({ item: { name, secondName, faxNumber, profileMediaId } }: FaxContactItemProps) => {
  const { t } = useTranslation('fax');
  return (
    <ListRow style={{ padding: theme.spacing(1), cursor: 'pointer' }}>
      <ListRow.Lead>
        <Avatar
          size='small'
          src={profileMediaId || undefined}
          css={css`
            svg {
              margin-right: 0;
            }
            figure {
              font-size: 0.6em;
            }
          `}
        />
      </ListRow.Lead>
      <ListRow.Content css={{ textAlign: 'initial' }}>
        <Text weight='bold'>{`${name ?? ''} ${secondName ?? ''}`}</Text>
        <Text color='subdued' size={'medium'}>
          {faxNumber ? phone(faxNumber, { hideCountry: true }) : t('No Phone Number')}
        </Text>
      </ListRow.Content>
    </ListRow>
  );
};

const ContactChip = ({
  children,
  onClick,
  disabled,
}: {
  children?: string;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const { selectedFax } = useSelectedFaxShallowStore('selectedFax');
  const { locationId } = selectedFax;
  const { setShow } = useSlidePanelShallowStore('setShow');
  const alert = useAlert();
  const { t } = useTranslation('fax');

  const childrenArray = children && children.length > 0 ? children.trim().split(' ') : [];
  const contactNumber = childrenArray.at(-1) ?? '';
  const contactName = childrenArray.slice(0, -1).join(' ') ?? '';

  return (
    <div
      onClick={() => {
        if (disabled) return;
        if (contactNumber) {
          setShow(true, 'faxContactForm', {
            contactNumber,
            locationId: locationId,
            action: FaxTypes.ContactAction.EDIT,
          });
        } else {
          alert.error(t('Error rendering contact data!'));
        }
      }}
    >
      <Chip.Removable
        css={css`
          width: auto;
          max-width: 160px;
          cursor: pointer;
        `}
        variant={!!disabled ? 'disabled' : 'default'}
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          onClick?.();
        }}
        disabled={!!disabled}
        leftElement={<Avatar name={/^\d+$/.test(contactName.trim()) ? '' : contactName} size='xxs' />}
      >
        {contactName
          ? contactName
          : /^\d+$/.test(contactNumber.trim())
          ? phone(contactNumber, { hideCountry: true })
          : t('Invalid Contact')}
      </Chip.Removable>
    </div>
  );
};

const AddContactMenuItem = ({ value }: { value: string }) => {
  let number = value.replace(/[\s-()]/g, '');
  if (number.startsWith('+1')) {
    number = number.slice(2);
  } else if (number.startsWith('1')) {
    number = number.slice(1);
  }

  return (
    <>
      <li
        css={{
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        {phone(value, { hideCountry: true })}
      </li>
    </>
  );
};
